import React, {
    useEffect,
    useRef,
    useState
} from "react";

import { useTranslation } from "react-i18next";

import {
    postForumTopic,
    updateForumTopic,
    uploadFileToForumTopic
} from "../../pages/forum/forumTopicService";

import {
    getLoggedInUser
} from "../../pages/userapp/ServiceUserApp";


export default function ForumTopicForm({
    topic,
    onClose,
    onSaved
}) {

    const { t } = useTranslation();

    // ==========================================================
    // Utilisateur connecté
    // ==========================================================

    const loggedUser = getLoggedInUser();


    // ==========================================================
    // Etat initial
    // ==========================================================

    const emptyTopic = {
        id: null,
        topicObject: "",
        topicBody: "",
        author: loggedUser,
        pinned: false,
        resolved: false

    };


    // ==========================================================
    // Etats
    // ==========================================================

    const [data, setData] =
        useState(emptyTopic);

    const [errors, setErrors] =
        useState({});

    const [loading, setLoading] =
        useState(false);


    // ==========================================================
    // Fichiers
    // ==========================================================

    const [files, setFiles] =
        useState([
            null,
            null,
            null,
            null
        ]);


    const [fileNames, setFileNames] =
        useState([
            "",
            "",
            "",
            ""
        ]);


    // ==========================================================
    // Références des inputs fichiers
    // ==========================================================

    const fileInputRefs = useRef([]);


    // ==========================================================
    // Nettoyage des données
    // ==========================================================

    const sanitizeData = () => {
        return {
            ...data,

            topicObject:
                data.topicObject
                    ? data.topicObject.trim()
                    : "",

            topicBody:
                data.topicBody
                    ? data.topicBody.trim()
                    : ""

        };

    };


    // ==========================================================
    // Chargement en modification
    // ==========================================================

    useEffect(() => {
        if (topic) {
            setData({
                ...emptyTopic,
                ...topic
            });
        }
        else {
            setData(emptyTopic);
        }
        // Réinitialisation des fichiers
        setFiles([
            null,
            null,
            null,
            null
        ]);

        setFileNames([
            "",
            "",
            "",
            ""
        ]);

        setErrors({});

    }, [topic]);


    // ==========================================================
    // Modification des champs
    // ==========================================================

    const handleChange = (e) => {
        const {
            name,
            value,
            checked,
            type
        } = e.target;


        setData(old => ({
            ...old,
            [name]:
                type === "checkbox"
                    ? checked
                    : value

        }));

    };

// ==========================================================
// Validation du formulaire
// ==========================================================

const validate = () => {
    const err = {};
    const values = sanitizeData();

    // ======================================================
    // Objet obligatoire
    // ======================================================

    if (
        values.topicObject.length === 0
    ) {
        err.topicObject =
            t(
                "forum.topicObjectRequired"
            );
    }

    // ======================================================
    // Objet minimum
    // ======================================================
    else if (
        values.topicObject.length < 5
    ) {
        err.topicObject =
            t(
                "forum.topicObjectMinLength"
            );

    }


    // ======================================================
    // Objet maximum
    // ======================================================

    else if (
        values.topicObject.length > 100
    ) {

        err.topicObject =
            t(
                "forum.topicObjectMaxLength"
            );

    }

    const topicBody =
        values.topicBody?.trim() || "";


    const hasMessage =
        topicBody.length > 0;


    const hasFiles =
        Array.isArray(files) &&
        files.some(
            (file) => file !== null
        );


    if (
        !hasMessage &&
        !hasFiles
    ) {

        err.topicBody =
            t(
                "forum.messageOrFileRequired"
            );

    }


    // ======================================================
    // Affectation des erreurs
    // ======================================================

    setErrors(err);


    // ======================================================
    // Résultat
    // ======================================================

    return (
        Object.keys(err).length === 0
    );

};
    // ==========================================================
    // Validation fichier
    // ==========================================================

    const isValidFile = (file) => {

        if (!file) {
            return true;
        }


const allowedTypes = [
    // Documents
    "application/pdf",

    // Images
    "image/jpeg",
    "image/jpg",
    "image/png",

    // Vidéos
    "video/mp4",
    "video/webm",
    "video/ogg",
    "video/quicktime",
    "video/x-msvideo"
];


        return allowedTypes.includes(
            file.type
        );

    };


    // ==========================================================
    // Ouverture du sélecteur de fichier
    // ==========================================================

    const openFileChooser = (index) => {
        if (
            fileInputRefs.current[index]
        ) {
            fileInputRefs.current[index].click();
        }

    };


    // ==========================================================
    // Sélection fichier
    // ==========================================================

    const handleFileChange = (
        index,
        event
    ) => {

        const file =
            event.target.files &&
            event.target.files.length > 0
                ? event.target.files[0]
                : null;


        // ------------------------------------------------------
        // Aucun fichier
        // ------------------------------------------------------

        if (!file) {
            return;

        }


        // ------------------------------------------------------
        // Validation format
        // ------------------------------------------------------

        if (
            !isValidFile(file)
        ) {

            alert(
                t(
                    "forum.invalidFileType"
                )
            );


            // Reset input
            if (
                fileInputRefs.current[index]
            ) {

                fileInputRefs.current[index]
                    .value = "";

            }

            return;

        }


        // ------------------------------------------------------
        // Mise à jour des fichiers
        // ------------------------------------------------------

        const newFiles = [
            ...files
        ];

        const newNames = [
            ...fileNames
        ];
        newFiles[index] = file;
        newNames[index] = file.name;
        setFiles(newFiles);
        setFileNames(newNames);

    };


    // ==========================================================
    // Suppression fichier
    // ==========================================================

    const removeFile = (index) => {
        const newFiles = [
            ...files
        ];

        const newNames = [
            ...fileNames
        ];


        newFiles[index] = null;
        newNames[index] = "";
        setFiles(newFiles);
        setFileNames(newNames);
        // ------------------------------------------------------
        // Reset input HTML
        // ------------------------------------------------------
        if (
            fileInputRefs.current[index]
        ) {
            fileInputRefs.current[index]
                .value = "";
        }

    };

    // ==========================================================
    // Sauvegarde
    // ==========================================================

    const handleSubmit = async (e) => {
        e.preventDefault();
        // ------------------------------------------------------
        // Validation
        // ------------------------------------------------------
        if (!validate()) {
            return;
        }
        try {
            setLoading(true);
            let response;
            // ==================================================
            // Création
            // ==================================================
            if (!data.id) {
                response =
                    await postForumTopic(
                        sanitizeData()
                    );
            }
            // ==================================================
            // Modification
            // ==================================================
            else {
                response =
                    await updateForumTopic(
                        sanitizeData()
                    );

            }
            // ==================================================
            // Sujet enregistré
            // ==================================================
            const savedTopic =
                response.data;
            // ==================================================
            // Upload des pièces jointes
            // ==================================================
            for (
                let i = 0;
                i < files.length;
                i++
            ) {
                if (files[i]) {
                    const formData =
                        new FormData();
                    formData.append(
                        "file",
                        files[i]
                    );
                    await uploadFileToForumTopic(
                        savedTopic.id,
                        i + 1,
                        formData
                    );
                }
            }


            // ==================================================
            // Callback
            // ==================================================

            if (onSaved) {

                onSaved(
                    savedTopic
                );

            }


            // ==================================================
            // Fermeture
            // ==================================================

            if (onClose) {

                onClose();

            }

        }

        catch (error) {

            console.error(
                "Erreur Axios :",
                error
            );


            if (
                error.response
            ) {

                alert(
                    JSON.stringify(
                        error.response.data
                    )
                );

            }

        }

        finally {

            setLoading(false);

        }

    };


    // ==========================================================
    // Rendu
    // ==========================================================

    return (

        <div
            className="modal fade show"
            style={{
                display: "block",
                background: "rgba(0,0,0,.45)"
            }}
        >

            <div className="modal-dialog modal-xl">

                <div className="modal-content">


                    {/* ==================================================
                        HEADER
                    ================================================== */}

                    <div
                        className="
                            modal-header
                            bg-success
                            text-white
                        "
                    >

                        <h4 className="modal-title">

                            <i
                                className="
                                    bi
                                    bi-chat-square-text
                                    me-2
                                "
                            />

                            {
                                data.id
                                    ? t(
                                        "forum.editTopic"
                                    )
                                    : t(
                                        "forum.newTopic"
                                    )
                            }

                        </h4>


                        <button

                            type="button"

                            className="
                                btn-close
                                btn-close-white
                            "

                            onClick={onClose}

                        />

                    </div>


                    {/* ==================================================
                        FORMULAIRE
                    ================================================== */}

                    <form
                        onSubmit={handleSubmit}
                    >

                        <div className="modal-body">

                            <div className="row">


                                {/* ==================================================
                                    OBJET
                                ================================================== */}

                                <div
                                    className="
                                        col-12
                                        mb-3
                                    "
                                >

                                    <label
                                        className="
                                            form-label
                                            fw-bold
                                        "
                                    >

                                        {t(
                                            "forum.objet"
                                        )}

                                    </label>


                                    <input

                                        type="text"

                                        className={
                                            errors.topicObject
                                                ? "form-control is-invalid"
                                                : "form-control"
                                        }

                                        name="topicObject"

                                        value={
                                            data.topicObject
                                        }

                                        onChange={
                                            handleChange
                                        }

                                        placeholder={
                                            t(
                                                "forum.topicObjectPlaceholder"
                                            )
                                        }

                                    />


                                    {
                                        errors.topicObject && (

                                            <div
                                                className="
                                                    invalid-feedback
                                                "
                                            >

                                                {
                                                    errors.topicObject
                                                }

                                            </div>

                                        )
                                    }

                                </div>


                                {/* ==================================================
                                    DESCRIPTION
                                ================================================== */}

                                <div
                                    className="
                                        col-12
                                        mb-4
                                    "
                                >

                                    <label
                                        className="
                                            form-label
                                            fw-bold
                                        "
                                    >

                                        {
                                            t(
                                                "forum.description"
                                            )
                                        }

                                    </label>


                                    <textarea

                                        rows="8"

                                        style={{
                                            minHeight:
                                                "220px"
                                        }}

                                        name="topicBody"

                                        value={
                                            data.topicBody
                                        }

                                        onChange={
                                            handleChange
                                        }

                                        className={
                                            errors.topicBody
                                                ? "form-control is-invalid"
                                                : "form-control"
                                        }

                                        placeholder={
                                            t(
                                                "forum.topicDescriptionPlaceholder"
                                            )
                                        }

                                    />


                                    {
                                        errors.topicBody && (

                                            <div
                                                className="
                                                    invalid-feedback
                                                "
                                            >

                                                {
                                                    errors.topicBody
                                                }

                                            </div>

                                        )
                                    }


                                    <small
                                        className="
                                            text-muted
                                        "
                                    >

                                        {
                                            t(
                                                "forum.descriptionHelp"
                                            )
                                        }

                                    </small>

                                </div>


                                {/* ==================================================
                                    PIECES JOINTES
                                ================================================== */}

                                <div className="col-12">
                                    <hr />
                                    <h5 className="mb-4">
                                        <i
                                            className="
                                                bi
                                                bi-paperclip
                                                me-2
                                            "
                                        />

                                        {
                                            t(
                                                "common.attachments"
                                            )
                                        }

                                    </h5>

                                </div>

                                {/* ==================================================
                                    FICHIERS 1 -> 4
                                ================================================== */}

                                {
                                    [0, 1, 2, 3].map(
                                        (index) => (

                                            <div
                                                className="
                                                    col-md-6
                                                    mb-4
                                                "
                                                key={index}
                                            >


                                                {/* ----------------------------------
                                                    Label
                                                ---------------------------------- */}

                                                <label
                                                    className="
                                                        form-label
                                                    "
                                                >

                                                    {
                                                        t(
                                                            "common.attachment"
                                                        )
                                                    }

                                                    {" "}

                                                    {
                                                        index + 1
                                                    }

                                                </label>


                                                {/* ----------------------------------
                                                    Input fichier caché
                                                ---------------------------------- */}

                                                <input
                                                    ref={(el) => {
                                                        fileInputRefs
                                                            .current[index] =
                                                            el;
                                                    }}
                                                    type="file"
                                                    hidden
                                                    accept="
                                                        .pdf,
                                                        .jpg,
                                                        .jpeg,
                                                        .png,
                                                        .mp4,
                                                        .webm,
                                                        .ogg               
                                                    "

                                                    name={
                                                        `attachedFile${index + 1}`
                                                    }

                                                    onChange={
                                                        (e) =>
                                                            handleFileChange(
                                                                index,
                                                                e
                                                            )
                                                    }

                                                />


                                                {/* ----------------------------------
                                                    Bouton + nom fichier
                                                ---------------------------------- */}

                                                <div
                                                    className="
                                                        input-group
                                                    "
                                                >


                                                    {/* ==============================
                                                        BOUTON
                                                    ============================== */}

                                                    <button

                                                        type="button"

                                                        className="
                                                            btn
                                                            btn-success
                                                        "

                                                        onClick={() =>
                                                            openFileChooser(
                                                                index
                                                            )
                                                        }

                                                        disabled={
                                                            loading
                                                        }

                                                    >

                                                        <i
                                                            className="
                                                                bi
                                                                bi-paperclip
                                                                me-2
                                                            "
                                                        />

                                                        {
                                                            t(
                                                                "common.chooseFile"
                                                            )
                                                        }

                                                    </button>


                                                    {/* ==============================
                                                        NOM DU FICHIER
                                                    ============================== */}

                                                    <span
                                                        className="
                                                            form-control
                                                            text-start
                                                        "
                                                    >

                                                        {
                                                            files[index]
                                                                ? (

                                                                    <>

                                                                        <i
                                                                            className="
                                                                                bi
                                                                                bi-file-earmark
                                                                                text-success
                                                                                me-2
                                                                            "
                                                                        />

                                                                        {
                                                                            fileNames[
                                                                                index
                                                                            ]
                                                                        }

                                                                    </>

                                                                )
                                                                : (

                                                                    <span
                                                                        className="
                                                                            text-muted
                                                                        "
                                                                    >

                                                                        {
                                                                            t(
                                                                                "common.noAttachment"
                                                                            )
                                                                        }

                                                                    </span>

                                                                )
                                                        }

                                                    </span>


                                                    {/* ==============================
                                                        SUPPRIMER
                                                    ============================== */}

                                                    {
                                                        files[index] && (

                                                            <button

                                                                type="button"

                                                                className="
                                                                    btn
                                                                    btn-outline-danger
                                                                "

                                                                onClick={() =>
                                                                    removeFile(
                                                                        index
                                                                    )
                                                                }

                                                                disabled={
                                                                    loading
                                                                }

                                                                title={
                                                                    t(
                                                                        "button.delete"
                                                                    )
                                                                }

                                                            >

                                                                <i
                                                                    className="
                                                                        bi
                                                                        bi-x-circle
                                                                    "
                                                                />

                                                            </button>

                                                        )
                                                    }

                                                </div>

                                            </div>

                                        )
                                    )
                                }


                                {/* ==================================================
                                    OPTIONS
                                ================================================== */}

                                <div
                                    className="
                                        col-md-6
                                        mt-3
                                    "
                                >

                                    <div
                                        className="
                                            form-check
                                        "
                                    >

                                        <input

                                            className="
                                                form-check-input
                                            "

                                            type="checkbox"

                                            id="resolved"

                                            name="resolved"

                                            checked={
                                                data.resolved
                                            }

                                            onChange={
                                                handleChange
                                            }

                                        />

                                        <label
                                            className="
                                                form-check-label
                                            "
                                            htmlFor="resolved"
                                        >

                                            {
                                                t(
                                                    "forum.resolveTopic"
                                                )
                                            }

                                        </label>

                                    </div>

                                </div>


                                <div
                                    className="
                                        col-md-6
                                        mt-3
                                    "
                                >

                                    <div
                                        className="
                                            form-check
                                        "
                                    >

                                        <input

                                            className="
                                                form-check-input
                                            "

                                            type="checkbox"

                                            id="pinned"

                                            name="pinned"

                                            checked={
                                                data.pinned
                                            }

                                            onChange={
                                                handleChange
                                            }

                                        />

                                        <label
                                            className="
                                                form-check-label
                                            "
                                            htmlFor="pinned"
                                        >

                                            {
                                                t(
                                                    "forum.pinTopic"
                                                )
                                            }

                                        </label>

                                    </div>

                                </div>

                            </div>

                        </div>


                        {/* ==================================================
                            FOOTER
                        ================================================== */}

                        <div className="modal-footer">


                            <button

                                type="button"

                                className="
                                    btn
                                    btn-secondary
                                "

                                onClick={
                                    onClose
                                }

                                disabled={
                                    loading
                                }

                            >

                                <i
                                    className="
                                        bi
                                        bi-x-circle
                                        me-2
                                    "
                                />

                                {
                                    t(
                                        "button.cancel"
                                    )
                                }

                            </button>


                            <button

                                type="submit"

                                className="
                                    btn
                                    btn-success
                                "

                                disabled={
                                    loading
                                }

                            >

                                {
                                    loading
                                        ? (

                                            <>

                                                <span
                                                    className="
                                                        spinner-border
                                                        spinner-border-sm
                                                        me-2
                                                    "
                                                />

                                                {
                                                    t(
                                                        "button.saving"
                                                    )
                                                }

                                            </>

                                        )
                                        : (

                                            <>

                                                <i
                                                    className="
                                                        bi
                                                        bi-check-circle
                                                        me-2
                                                    "
                                                />

                                                {
                                                    t(
                                                        "button.save"
                                                    )
                                                }

                                            </>

                                        )
                                }

                            </button>

                        </div>

                    </form>

                </div>

            </div>

        </div>

    );

}